// SplitButton — a responsive toolbar action: a labelled rectangle where there's
// room (md+), a compact SQUARE icon button below md — the shell's hamburger
// threshold — so the WHOLE list toolbar (filter · export · create) collapses to
// icons at the SAME width, instead of buttons shrinking at staggered breakpoints.
// One action fires directly; multiple actions open a dropdown (a caret hints it
// on md+; the square opens it below md). It is the shared shape behind
// CreateButton (the primary "add" action), so the title-band primary collapses to
// an icon WITH the ⋯ Actions menu at one width (COMPONENTS §Catalog). Modes:
//   • single  → `onPress` (or a lone `actions` entry — its `disabled` honoured) fires directly
//   • menu    → ≥2 `actions` render a dropdown; our `Menu` owns the a11y/keyboard
// `loading` + `disabled` mirror <Button> (loading swaps the icon for a spinner
// and blocks input). The mobile square matches the 40×40 footprint of the
// toolbar's other icon buttons (filter, gear). `collapseAt` picks the square's
// breakpoint: `md` (default — the list title band, snapping with the ⋯ Actions
// door + the hamburger) or `sm` (an entity header's seat, beside the entity's
// ⋯ door, which squares at sm).

import type { ReactNode } from 'react';
import { icons } from './icons.js';
import { Button } from './Button.js';
import { Menu, MenuItem, MenuSeparator } from './Menu.js';

export interface SplitButtonAction {
  id: string;
  label: string;
  icon?: ReactNode;
  onAction: () => void;
  tone?: 'default' | 'danger';
  /** Visual group — a separator is inserted in the menu where this changes. */
  group?: string;
  /** Shown, not actionable (a batch outcome nothing in the selection is eligible for). */
  disabled?: boolean;
  /** Why it is disabled — the row's second line. */
  note?: string;
}

export interface SplitButtonProps {
  /** Visible label (sm+) AND the accessible name of the square mobile / menu trigger. */
  label: string;
  /** Leading icon — shown at every size (it is the whole button on mobile). */
  icon: ReactNode;
  variant?: 'primary' | 'secondary';
  /** Single-action handler — used when there are no `actions` (or exactly one). */
  onPress?: () => void;
  /** Choices. ≥2 → a dropdown; exactly 1 → fires it directly; 0/undefined → `onPress`. */
  actions?: SplitButtonAction[];
  /** Accessible name for the dropdown (menu mode). Defaults to `label`. */
  menuLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  /** The labelled→square collapse point. `md` (default) = the list title band;
   *  `sm` = the entity header (its ⋯ Actions door squares at sm). */
  collapseAt?: 'sm' | 'md';
}

// Square below md (the hamburger threshold): drop the horizontal padding and pin
// the width to the min-height (2.5rem) so the centered icon reads as a 40×40
// square; the label + caret are md+-only, so below md only the icon (or spinner)
// shows — matching the filter's icon collapse + the gear so the toolbar is
// uniform.
const RESPONSIVE_SHAPE: Record<'sm' | 'md', string> = {
  md: 'max-md:w-10 max-md:px-0 max-md:gap-0',
  sm: 'max-sm:w-10 max-sm:px-0 max-sm:gap-0',
};
const LABEL_FROM: Record<'sm' | 'md', string> = { md: 'hidden md:inline', sm: 'hidden sm:inline' };

export function SplitButton({
  label,
  icon,
  variant = 'primary',
  onPress,
  actions,
  menuLabel,
  disabled = false,
  loading = false,
  collapseAt = 'md',
}: SplitButtonProps) {
  const isMenu = (actions?.length ?? 0) >= 2;
  const single = onPress ?? actions?.[0]?.onAction;
  // A lone action fires directly — so its own `disabled` is the button's.
  const lone = !isMenu && !onPress ? actions?.[0] : undefined;

  const trigger = (asMenu: boolean) => (
    <Button
      variant={variant}
      aria-label={label}
      disabled={disabled || Boolean(lone?.disabled)}
      loading={loading}
      className={RESPONSIVE_SHAPE[collapseAt]}
      onClick={asMenu ? undefined : single}
    >
      {!loading && icon}
      <span className={LABEL_FROM[collapseAt]}>{label}</span>
      {asMenu && <icons.caretDown size={14} aria-hidden className={LABEL_FROM[collapseAt]} />}
    </Button>
  );

  if (!isMenu) return trigger(false);

  const items: ReactNode[] = [];
  actions!.forEach((a, i) => {
    if (i > 0 && a.group !== actions![i - 1]!.group) items.push(<MenuSeparator key={`sep-${a.id}`} />);
    items.push(
      <MenuItem key={a.id} icon={a.icon} tone={a.tone} onAction={a.onAction} isDisabled={a.disabled} description={a.disabled ? a.note : undefined}>
        {a.label}
      </MenuItem>,
    );
  });

  return (
    <Menu label={menuLabel ?? label} placement="bottom end" renderTrigger={() => trigger(true)}>
      {items}
    </Menu>
  );
}
